import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { invalidateRecordAuthorization } from './recordAuthorizationQuery'
import type { ClearanceEntity } from '@omnea-app/authz'

type UpdateRecordConfidentialityParams = {
  entity: ClearanceEntity
  recordId: string
  /** The record's own detail keys. Record access is invalidated here, since a flip can revoke the marker's own access. */
  invalidateKeys: QueryKey[]
  /** Names the record, so the toast comes from the caller's own namespace. */
  errorMessage: string
}

/**
 * Marks or unmarks one record as confidential. The failure toast is the only feedback on error:
 * the menu and its confirmation close on submit, so without it a failed flip is silent.
 */
export const useUpdateRecordConfidentiality = ({
  entity,
  recordId,
  invalidateKeys,
  errorMessage,
}: UpdateRecordConfidentialityParams) => {
  const { getToken } = useAuthenticatedUser()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: async (isConfidential: boolean) => {
      const authorization = await getToken()
      const permissionsClient = new PermissionsHttpClient({ authorization })
      const { data, error } = await permissionsClient.POST('/entities/{entity}/{recordId}/confidentiality', {
        params: { path: { entity, recordId } },
        body: { isConfidential },
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async () => {
      await Promise.all([
        ...invalidateKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        invalidateRecordAuthorization(queryClient),
      ])
    },
    onError: () => {
      enqueueSnackbar(errorMessage, { variant: 'error' })
    },
  })
}
